import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  IconButton,
  Alert,
  Stack,
} from '@mui/material';
import {
  People,
  Work,
  CalendarToday,
  Add,
  Visibility,
  Sync,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DashboardData } from '../types';
import { reportsApi, PIJobSyncResults } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import MiniPIGraph from '../components/MiniPIGraph';
import { WorkflowSummary } from '../components/Workflow';
import PIJobSyncDialog from '../components/PIJobSyncDialog';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const navigate = useNavigate();

  const canCreateJob = user?.role === 'admin' || user?.role === 'manager';
  const canImport = user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager';

  const handleSyncComplete = (results: PIJobSyncResults) => {
    const { jobs, candidates } = results;
    const parts = [];
    if (jobs.created > 0) parts.push(`${jobs.created} jobs created`);
    if (jobs.linked > 0) parts.push(`${jobs.linked} jobs linked`);
    if (candidates.imported > 0) parts.push(`${candidates.imported} candidates imported`);

    if (parts.length > 0) {
      showNotification(`Import complete: ${parts.join(', ')}`, 'success');
      fetchDashboardData(); // Refresh dashboard data
    } else {
      showNotification('No changes were made', 'info');
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await reportsApi.getDashboard();
      setDashboardData(response.data);
    } catch (error) {
      showNotification('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getMatchColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<string, { color: any; label: string }> = {
      open: { color: 'success', label: 'Open' },
      paused: { color: 'warning', label: 'Paused' },
      closed: { color: 'default', label: 'Closed' },
      filled: { color: 'info', label: 'Filled' }
    };
    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Typography variant="h6" color="error">
        Failed to load dashboard data
      </Typography>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Dashboard</Typography>
        <Stack direction="row" spacing={2}>
          {canCreateJob && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/jobs/new')}
            >
              New Job
            </Button>
          )}
          {canImport && (
            <Button
              variant="outlined"
              startIcon={<Sync />}
              onClick={() => setSyncDialogOpen(true)}
            >
              Import from PI
            </Button>
          )}
        </Stack>
      </Box>

      {/* Pipeline Overview */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
        gap={3}
        mb={3}
      >
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Open Positions
                </Typography>
                <Typography variant="h3">
                  {dashboardData.pipelineOverview.openJobs}
                </Typography>
              </Box>
              <Work fontSize="large" color="primary" />
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Active Candidates
                </Typography>
                <Typography variant="h3">
                  {dashboardData.pipelineOverview.totalCandidatesInPipeline}
                </Typography>
              </Box>
              <People fontSize="large" color="primary" />
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Interviews This Week
                </Typography>
                <Typography variant="h3">
                  {dashboardData.pipelineOverview.interviewsThisWeek}
                </Typography>
              </Box>
              <CalendarToday fontSize="large" color="primary" />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Action Items */}
      {(dashboardData.actionItems.candidatesAwaitingReview > 0 || 
        dashboardData.actionItems.overdueInterviews > 0) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
            Action Required
          </Typography>
          <Stack spacing={1}>
            {dashboardData.actionItems.candidatesAwaitingReview > 0 && (
              <Typography variant="body2">
                • {dashboardData.actionItems.candidatesAwaitingReview} candidates with completed assessments awaiting review
              </Typography>
            )}
            {dashboardData.actionItems.overdueInterviews > 0 && (
              <Typography variant="body2">
                • {dashboardData.actionItems.overdueInterviews} overdue interviews need attention
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      {/* Active Hiring Workflows - only show if enabled */}
      {process.env.REACT_APP_ENABLE_WORKFLOWS === 'true' && (
        <Box mb={3}>
          <WorkflowSummary limit={5} showViewAll={true} />
        </Box>
      )}

      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', lg: '1fr 1fr' }}
        gap={3}
      >
        {/* Top Candidate Matches */}
        <Paper sx={{ p: 2, height: '100%' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Top Candidate Matches</Typography>
            <Button size="small" onClick={() => navigate('/candidates')}>
              View All
            </Button>
          </Box>
          <Stack spacing={2}>
            {dashboardData.topMatches.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No candidates with PI assessments yet
              </Typography>
            ) : (
              dashboardData.topMatches.map(candidate => (
                <Paper key={candidate.id} variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {candidate.first_name} {candidate.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {candidate.job_title} • {candidate.pi_profile_name || 'Unknown Profile'}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box sx={{ 
                        width: 140, 
                        height: 120, 
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <MiniPIGraph
                          factorA={candidate.pi_a}
                          factorB={candidate.pi_b}
                          factorC={candidate.pi_c}
                          factorD={candidate.pi_d}
                          idealRanges={{
                            a: candidate.pi_a_target as [number, number],
                            b: candidate.pi_b_target as [number, number],
                            c: candidate.pi_c_target as [number, number],
                            d: candidate.pi_d_target as [number, number]
                          }}
                          size="small"
                          showLabels={false}
                        />
                      </Box>
                      <Chip
                        label={`${Math.round(candidate.pi_score)}%`}
                        color={getMatchColor(candidate.pi_score)}
                        size="small"
                      />
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/candidates/${candidate.id}`)}
                      >
                        <Visibility />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </Stack>
        </Paper>

        {/* Jobs Performance */}
        <Paper sx={{ p: 2, height: '100%' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Jobs Pipeline Health</Typography>
            <Button size="small" onClick={() => navigate('/jobs')}>
              View All
            </Button>
          </Box>
          <Stack spacing={2}>
            {dashboardData.jobsPipeline.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No open jobs yet
              </Typography>
            ) : (
              dashboardData.jobsPipeline.map(job => (
                <Paper key={job.id} variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {job.title}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {job.days_open} days open
                      </Typography>
                      {getStatusChip(job.status)}
                    </Stack>
                  </Box>
                  <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Candidates
                      </Typography>
                      <Typography variant="h6">
                        {job.total_candidates}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Assessed
                      </Typography>
                      <Typography variant="h6">
                        {job.with_assessment}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Interviewed
                      </Typography>
                      <Typography variant="h6">
                        {job.interviewed}
                      </Typography>
                    </Box>
                  </Box>
                  <Box mt={1}>
                    <LinearProgress
                      variant="determinate"
                      value={job.total_candidates > 0 
                        ? (job.with_assessment / job.total_candidates) * 100 
                        : 0}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                </Paper>
              ))
            )}
          </Stack>
        </Paper>
      </Box>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
        gap={3}
        mt={3}
      >
        {/* Assessment Coverage */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            PI Assessment Coverage
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="center" py={2}>
            <Box position="relative" display="inline-flex">
              <CircularProgress
                variant="determinate"
                value={dashboardData.assessmentCoverage.percentage}
                size={120}
                thickness={4}
              />
              <Box
                top={0}
                left={0}
                bottom={0}
                right={0}
                position="absolute"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="h4" component="div" color="text.secondary">
                  {dashboardData.assessmentCoverage.percentage}%
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary">
              {dashboardData.assessmentCoverage.withAssessment} of {dashboardData.assessmentCoverage.total} candidates assessed
            </Typography>
          </Box>
        </Paper>

        {/* Profile Distribution */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Candidate Profile Distribution
          </Typography>
          {dashboardData.profileDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={dashboardData.profileDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, count }) => `${name} (${count})`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {dashboardData.profileDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No profile data available yet
            </Typography>
          )}
        </Paper>
      </Box>

      {/* PI Job Sync Dialog */}
      <PIJobSyncDialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        onComplete={handleSyncComplete}
      />
    </Box>
  );
};

export default Dashboard;